import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { createHelpBookTheme } from '../styles/theme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a CustomThemeProvider');
  }
  return context;
};

export const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState('light');
  const [primaryColor, setPrimaryColor] = useState('default');

  // Load theme preferences from localStorage
  useEffect(() => {
    const savedMode = localStorage.getItem('helpbook_theme_mode');
    const savedColor = localStorage.getItem('helpbook_primary_color');
    
    if (savedMode) {
      setMode(savedMode);
    } else {
      // Auto-detect system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setMode(prefersDark ? 'dark' : 'light');
    }
    
    if (savedColor) {
      if (savedColor === '#2166C1') {
        setPrimaryColor('default');
      } else {
        setPrimaryColor(savedColor);
      }
    }
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('helpbook_theme_mode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Create MUI theme
  const theme = createHelpBookTheme(mode, primaryColor);

  // Theme control functions
  const toggleMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('helpbook_theme_mode', newMode);
  };

  const setThemeMode = (newMode) => {
    setMode(newMode);
    localStorage.setItem('helpbook_theme_mode', newMode);
  };

  const changePrimaryColor = (color) => {
    setPrimaryColor(color);
    localStorage.setItem('helpbook_primary_color', color);
  };

  const resetTheme = () => {
    setMode('light');
    setPrimaryColor('default');
    localStorage.removeItem('helpbook_theme_mode');
    localStorage.removeItem('helpbook_primary_color');
  };

  const value = {
    mode,
    primaryColor,
    theme,
    toggleMode,
    setThemeMode,
    changePrimaryColor,
    resetTheme,
    isDark: mode === 'dark',
    isLight: mode === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
